import { useState, useMemo } from 'react';
import {
  Plus,
  Search,
  MoreHorizontal,
  Building2,
  Users,
  DollarSign,
  Archive,
  Trash2,
  Pencil,
  Globe,
  Mail,
  Phone,
  Download,
  Upload,
  Sparkles,
  X,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useCompanies } from '@/lib/hooks/leads/useCompanies';
import { useImportExport } from '@/lib/hooks/leads/useImportExport';
import { useLeadsAI } from '@/lib/hooks/leads/useAI';
import { useBulkActions } from '@/lib/hooks/leads/useBulkActions';
import type { LeadsCompany, LeadsCreateCompanyInput } from '@/lib/api';
import { CustomFieldEditor } from './CustomFieldEditor';
import { TimelinePanel } from './TimelinePanel';
import { ImportDialog } from './ImportDialog';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];

const emptyForm: LeadsCreateCompanyInput = {
  name: '',
  domain: '',
  industry: '',
  size: undefined,
  phone: '',
  email: '',
  website: '',
  notes: '',
  tags: [],
};

export function CompaniesView() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<LeadsCompany | null>(null);
  const [form, setForm] = useState<LeadsCreateCompanyInput>({ ...emptyForm });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const { companies, total, loading, createCompany, updateCompany, deleteCompany, archiveCompany } =
    useCompanies({ search: debouncedSearch, sortBy: 'name', sortDir: 'asc' });
  const { exportEntities, exporting } = useImportExport();
  const { generateSummary, loading: aiLoading } = useLeadsAI();
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    bulkDeleteCompanies,
    loading: bulkLoading,
  } = useBulkActions();

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  function openCreate() {
    setEditingCompany(null);
    setForm({ ...emptyForm });
    setTagInput('');
    setShowForm(true);
  }

  function openEdit(company: LeadsCompany) {
    setEditingCompany(company);
    setForm({
      name: company.name,
      domain: company.domain ?? '',
      industry: company.industry ?? '',
      size: company.size ?? undefined,
      phone: company.phone ?? '',
      email: company.email ?? '',
      website: company.website ?? '',
      notes: company.notes ?? '',
      tags: company.tags ?? [],
      metadata: company.metadata ?? {},
    });
    setTagInput('');
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({
        title: 'Validation',
        description: 'Company name is required',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, form);
        toast({ title: 'Company updated' });
      } else {
        await createCompany(form);
        toast({ title: 'Company created' });
      }
      setShowForm(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to save company', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(company: LeadsCompany) {
    try {
      await deleteCompany(company.id);
      toast({ title: 'Company moved to trash' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete company', variant: 'destructive' });
    }
  }

  async function handleArchive(company: LeadsCompany) {
    try {
      await archiveCompany(company.id);
      toast({ title: company.status === 'archived' ? 'Company unarchived' : 'Company archived' });
    } catch {
      toast({ title: 'Error', description: 'Failed to archive company', variant: 'destructive' });
    }
  }

  function handleTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!form.tags?.includes(tagInput.trim())) {
        setForm({ ...form, tags: [...(form.tags ?? []), tagInput.trim()] });
      }
      setTagInput('');
    }
  }

  function removeTag(tag: string) {
    setForm({ ...form, tags: form.tags?.filter((t) => t !== tag) });
  }

  return (
    <div>
      {/* Bulk Action Bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-6 py-3 bg-primary/5 border-b">
          <Checkbox
            checked={selectedCount === companies.length}
            onCheckedChange={(checked) => {
              if (checked) selectAll(companies.map((c) => c.id));
              else clearSelection();
            }}
          />
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkDeleteCompanies()}
              disabled={bulkLoading}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={clearSelection}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-6 pt-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground">{total} companies</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportEntities('companies')}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {/* Company List */}
      {loading ? (
        <div className="grid gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No companies yet</p>
          <p className="text-sm mt-1">Add your first company to get started</p>
          <Button variant="outline" className="mt-4" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {companies.map((company) => (
            <Card key={company.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="p-4 flex items-center justify-between">
                <Checkbox
                  checked={selectedIds.has(company.id)}
                  onCheckedChange={() => toggleSelect(company.id)}
                  className="mr-3 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{company.name}</h3>
                    {company.industry && (
                      <Badge variant="secondary" className="text-xs">
                        {company.industry}
                      </Badge>
                    )}
                    {company.size && (
                      <Badge variant="outline" className="text-xs">
                        {company.size}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-sm text-muted-foreground">
                    {company.domain && (
                      <span className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {company.domain}
                      </span>
                    )}
                    {company.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {company.email}
                      </span>
                    )}
                    {company.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {company.phone}
                      </span>
                    )}
                  </div>
                  {company.tags.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {company.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 ml-4">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1" title="Contacts">
                      <Users className="h-3.5 w-3.5" />
                      {company.contactCount ?? 0}
                    </span>
                    <span className="flex items-center gap-1" title="Deals">
                      <DollarSign className="h-3.5 w-3.5" />
                      {company.dealCount ?? 0}
                    </span>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(company)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleArchive(company)}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => generateSummary('company', company.id)}
                        disabled={aiLoading}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        AI Summary
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(company)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className={editingCompany ? 'sm:max-w-3xl' : 'sm:max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="company-name">Name *</Label>
              <Input
                id="company-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Corp"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="company-domain">Domain</Label>
                <Input
                  id="company-domain"
                  value={form.domain ?? ''}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                  placeholder="acme.com"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="company-industry">Industry</Label>
                <Input
                  id="company-industry"
                  value={form.industry ?? ''}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  placeholder="Technology"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Size</Label>
                <Select
                  value={form.size ?? ''}
                  onValueChange={(v) => setForm({ ...form, size: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s} employees
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="company-phone">Phone</Label>
                <Input
                  id="company-phone"
                  value={form.phone ?? ''}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="company-email">Email</Label>
                <Input
                  id="company-email"
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="company-website">Website</Label>
                <Input
                  id="company-website"
                  value={form.website ?? ''}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="company-notes">Notes</Label>
              <Textarea
                id="company-notes"
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1 mb-1">
                {form.tags?.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => removeTag(tag)}
                  >
                    {tag} &times;
                  </Badge>
                ))}
              </div>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Type tag and press Enter"
              />
            </div>
          </div>

          <CustomFieldEditor
            entityType="company"
            metadata={form.metadata ?? {}}
            onChange={(metadata) => setForm({ ...form, metadata })}
          />

          {editingCompany && (
            <div className="border-t pt-4 mt-2">
              <TimelinePanel companyId={editingCompany.id} limit={10} />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingCompany ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        entityType="companies"
        onImportComplete={() => {
          setShowImport(false);
        }}
      />
    </div>
  );
}
